/**
 * Centralized Theme Configuration System
 * Defines all theme layouts with professional color combinations
 * Theme layout is controlled via .env: REACT_APP_DEFAULT_THEME
 * Dark/Light mode is controlled by user from UI
 */

export const THEMES = {
  default: {
    name: 'Default',
    description: 'Original application colors',
    light: {
      // Backgrounds
      bgPrimary: '#f3f4f6',
      bgSecondary: '#e5e7eb',
      bgTertiary: '#d1d5db',
      bgCard: '#ffffff',
      bgModal: 'rgba(255, 255, 255, 0.95)',
      bgOverlay: 'rgba(0, 0, 0, 0.5)',
      
      // Text
      textPrimary: '#111827',
      textSecondary: '#4b5563',
      textTertiary: '#9ca3af',
      textLink: '#1e40af',
      textLinkHover: '#1e3a8a',
      
      // Borders
      borderPrimary: '#e5e7eb',
      borderSecondary: '#d1d5db',
      borderFocus: '#1e40af',
      
      // Brand Colors (Original blue)
      brandPrimary: '#1e599b',
      brandSecondary: '#164578',
      brandAccent: '#f59e0b',
      brandGradient: 'linear-gradient(135deg, #164578 0%, #1e599b 100%)',
      
      // Inputs
      inputBg: '#ffffff',
      inputBorder: '#d1d5db',
      inputBorderFocus: '#1e40af',
      inputText: '#111827',
      inputPlaceholder: '#9ca3af',
      
      // Buttons - Premium contrast colors
      buttonPrimaryBg: 'linear-gradient(135deg, #164578 0%, #1e599b 100%)',
      buttonPrimaryText: '#ffffff',
      buttonSecondaryBg: '#ffffff',
      buttonSecondaryText: '#1e599b',
      buttonSecondaryBorder: '#1e599b',
      buttonSuccessBg: 'linear-gradient(135deg, #059669 0%, #10b981 100%)',
      buttonSuccessText: '#ffffff',
      buttonDangerBg: 'linear-gradient(135deg, #dc2626 0%, #ef4444 100%)',
      buttonDangerText: '#ffffff',
      buttonDisabledBg: '#e5e7eb',
      buttonDisabledText: '#9ca3af',
      
      // Icon backgrounds
      iconBg: 'linear-gradient(135deg, #164578 0%, #1e599b 100%)',
      iconText: '#ffffff',
      
      // Shadows
      shadowCard: '0 1px 3px rgba(0, 0, 0, 0.1)',
      shadowElevated: '0 4px 6px rgba(0, 0, 0, 0.1)',
      shadowButton: '0 2px 4px rgba(30, 89, 155, 0.2)',
      
      // Status
      success: '#10b981',
      error: '#ef4444',
      warning: '#f59e0b',
    },
    dark: {
      // Backgrounds
      bgPrimary: '#111827',
      bgSecondary: '#1f2937',
      bgTertiary: '#374151',
      bgCard: '#1f2937',
      bgModal: 'rgba(31, 41, 55, 0.95)',
      bgOverlay: 'rgba(0, 0, 0, 0.7)',
      
      // Text
      textPrimary: '#f9fafb',
      textSecondary: '#d1d5db',
      textTertiary: '#9ca3af',
      textLink: '#60a5fa',
      textLinkHover: '#93c5fd',
      
      // Borders
      borderPrimary: '#374151',
      borderSecondary: '#4b5563',
      borderFocus: '#60a5fa',
      
      // Brand Colors
      brandPrimary: '#3b82f6',
      brandSecondary: '#2563eb',
      brandAccent: '#f59e0b',
      brandGradient: 'linear-gradient(135deg, #2563eb 0%, #3b82f6 100%)',
      
      // Inputs
      inputBg: '#1f2937',
      inputBorder: '#374151',
      inputBorderFocus: '#60a5fa',
      inputText: '#f9fafb',
      inputPlaceholder: '#6b7280',
      
      // Buttons - Premium contrast colors
      buttonPrimaryBg: 'linear-gradient(135deg, #2563eb 0%, #3b82f6 100%)',
      buttonPrimaryText: '#ffffff',
      buttonSecondaryBg: '#1f2937',
      buttonSecondaryText: '#3b82f6',
      buttonSecondaryBorder: '#3b82f6',
      buttonSuccessBg: 'linear-gradient(135deg, #059669 0%, #10b981 100%)',
      buttonSuccessText: '#ffffff',
      buttonDangerBg: 'linear-gradient(135deg, #dc2626 0%, #ef4444 100%)',
      buttonDangerText: '#ffffff',
      buttonDisabledBg: '#374151',
      buttonDisabledText: '#6b7280',
      
      // Icon backgrounds
      iconBg: 'linear-gradient(135deg, #2563eb 0%, #3b82f6 100%)',
      iconText: '#ffffff',
      
      // Shadows
      shadowCard: '0 1px 3px rgba(0, 0, 0, 0.3)',
      shadowElevated: '0 4px 6px rgba(0, 0, 0, 0.4)',
      shadowButton: '0 2px 4px rgba(59, 130, 246, 0.3)',
      
      // Status
      success: '#34d399',
      error: '#f87171',
      warning: '#fbbf24',
    },
  },
  
  obsidian: {
    name: 'Obsidian Luxury',
    description: 'Premium charcoal black with metallic gold-orange accents',
    light: {
      // Backgrounds (Using lighter variants for light mode while maintaining the palette)
      bgPrimary: '#E8EAED',
      bgSecondary: '#D1D5DB',
      bgTertiary: '#C0C5CC',
      bgCard: '#F3F4F6',
      bgModal: 'rgba(243, 244, 246, 0.98)',
      bgOverlay: 'rgba(13, 14, 18, 0.5)',
      
      // Text
      textPrimary: '#0D0E12',
      textSecondary: '#1A1C23',
      textTertiary: '#4B5563',
      textLink: '#FFD700',
      textLinkHover: '#FFA500',
      
      // Borders
      borderPrimary: '#D1D5DB',
      borderSecondary: '#C0C5CC',
      borderFocus: '#FFD700',
      
      // Brand Colors - Eye-catching golden-orange gradient
      brandPrimary: '#FFD700',
      brandSecondary: '#FFA500',
      brandAccent: '#FF8C00',
      brandGradient: 'linear-gradient(135deg, #FFD700 0%, #FFA500 50%, #FF8C00 100%)',
      
      // Inputs
      inputBg: '#F3F4F6',
      inputBorder: '#D1D5DB',
      inputBorderFocus: '#FFD700',
      inputText: '#0D0E12',
      inputPlaceholder: '#6B7280',
      
      // Buttons - Premium golden-orange gradient with dark text for contrast
      buttonPrimaryBg: 'linear-gradient(135deg, #FFD700 0%, #FFA500 50%, #FF8C00 100%)',
      buttonPrimaryText: '#0D0E12',
      buttonSecondaryBg: '#0D0E12',
      buttonSecondaryText: '#FFD700',
      buttonSecondaryBorder: '#FFD700',
      buttonSuccessBg: 'linear-gradient(135deg, #059669 0%, #10b981 100%)',
      buttonSuccessText: '#ffffff',
      buttonDangerBg: 'linear-gradient(135deg, #dc2626 0%, #ef4444 100%)',
      buttonDangerText: '#ffffff',
      buttonDisabledBg: '#D1D5DB',
      buttonDisabledText: '#6B7280',
      
      // Icon backgrounds - Golden-orange gradient
      iconBg: 'linear-gradient(135deg, #FFD700 0%, #FFA500 50%, #FF8C00 100%)',
      iconText: '#0D0E12',
      
      // Shadows
      shadowCard: '0 2px 8px rgba(13, 14, 18, 0.08)',
      shadowElevated: '0 8px 24px rgba(13, 14, 18, 0.12)',
      shadowButton: '0 4px 12px rgba(255, 215, 0, 0.35)',
      
      // Status
      success: '#10B981',
      error: '#EF4444',
      warning: '#FFD700',
    },
    dark: {
      // Backgrounds (60% Base, 30% Secondary)
      bgPrimary: '#0D0E12',
      bgSecondary: '#1A1C23',
      bgTertiary: '#252830',
      bgCard: '#1A1C23',
      bgModal: 'rgba(26, 28, 35, 0.98)',
      bgOverlay: 'rgba(0, 0, 0, 0.85)',
      
      // Text (Off-White)
      textPrimary: '#F3F4F6',
      textSecondary: '#E5E7EB',
      textTertiary: '#9CA3AF',
      textLink: '#FFD700',
      textLinkHover: '#FFA500',
      
      // Borders
      borderPrimary: '#252830',
      borderSecondary: '#3A3D45',
      borderFocus: '#FFD700',
      
      // Brand Colors - Eye-catching golden-orange gradient
      brandPrimary: '#FFD700',
      brandSecondary: '#FFA500',
      brandAccent: '#FF8C00',
      brandGradient: 'linear-gradient(135deg, #FFD700 0%, #FFA500 50%, #FF8C00 100%)',
      
      // Inputs
      inputBg: '#1A1C23',
      inputBorder: '#252830',
      inputBorderFocus: '#FFD700',
      inputText: '#F3F4F6',
      inputPlaceholder: '#6B7280',
      
      // Buttons - Premium golden-orange gradient with dark text for contrast
      buttonPrimaryBg: 'linear-gradient(135deg, #FFD700 0%, #FFA500 50%, #FF8C00 100%)',
      buttonPrimaryText: '#0D0E12',
      buttonSecondaryBg: '#252830',
      buttonSecondaryText: '#FFD700',
      buttonSecondaryBorder: '#FFD700',
      buttonSuccessBg: 'linear-gradient(135deg, #059669 0%, #10b981 100%)',
      buttonSuccessText: '#ffffff',
      buttonDangerBg: 'linear-gradient(135deg, #dc2626 0%, #ef4444 100%)',
      buttonDangerText: '#ffffff',
      buttonDisabledBg: '#252830',
      buttonDisabledText: '#6B7280',
      
      // Icon backgrounds - Golden-orange gradient
      iconBg: 'linear-gradient(135deg, #FFD700 0%, #FFA500 50%, #FF8C00 100%)',
      iconText: '#0D0E12',
      
      // Shadows (Soft subtle shadows with golden glow)
      shadowCard: '0 2px 8px rgba(0, 0, 0, 0.4)',
      shadowElevated: '0 8px 24px rgba(0, 0, 0, 0.5)',
      shadowButton: '0 4px 16px rgba(255, 215, 0, 0.4)',
      
      // Status
      success: '#34D399',
      error: '#F87171',
      warning: '#FFD700',
    },
  },
  
  ocean: {
    name: 'Ocean',
    description: 'Professional blue gradient theme',
    light: {
      // Backgrounds
      bgPrimary: '#ffffff',
      bgSecondary: '#f0f4f8',
      bgTertiary: '#e2e8f0',
      bgCard: '#ffffff',
      bgModal: 'rgba(255, 255, 255, 0.95)',
      bgOverlay: 'rgba(0, 0, 0, 0.5)',
      
      // Text
      textPrimary: '#1a202c',
      textSecondary: '#4a5568',
      textTertiary: '#718096',
      textLink: '#3182ce',
      textLinkHover: '#2c5282',
      
      // Borders
      borderPrimary: '#e2e8f0',
      borderSecondary: '#cbd5e0',
      borderFocus: '#3182ce',
      
      // Brand Colors
      brandPrimary: '#09579a',
      brandSecondary: '#063c6c',
      brandAccent: '#ed8936',
      brandGradient: 'linear-gradient(135deg, #063c6c 0%, #09579a 50%, #ed8936 100%)',
      
      // Inputs
      inputBg: '#ffffff',
      inputBorder: '#e2e8f0',
      inputBorderFocus: '#3182ce',
      inputText: '#1a202c',
      inputPlaceholder: '#a0aec0',
      
      // Buttons - Premium contrast colors
      buttonPrimaryBg: 'linear-gradient(135deg, #063c6c 0%, #09579a 50%, #ed8936 100%)',
      buttonPrimaryText: '#ffffff',
      buttonSecondaryBg: '#ffffff',
      buttonSecondaryText: '#09579a',
      buttonSecondaryBorder: '#09579a',
      buttonSuccessBg: 'linear-gradient(135deg, #059669 0%, #10b981 100%)',
      buttonSuccessText: '#ffffff',
      buttonDangerBg: 'linear-gradient(135deg, #dc2626 0%, #ef4444 100%)',
      buttonDangerText: '#ffffff',
      buttonDisabledBg: '#e2e8f0',
      buttonDisabledText: '#a0aec0',
      
      // Icon backgrounds
      iconBg: 'linear-gradient(135deg, #063c6c 0%, #09579a 100%)',
      iconText: '#ffffff',
      
      // Shadows
      shadowCard: '0 4px 20px rgba(0, 0, 0, 0.08)',
      shadowElevated: '0 10px 40px rgba(0, 0, 0, 0.12)',
      shadowButton: '0 4px 12px rgba(9, 87, 154, 0.3)',
      
      // Status
      success: '#38a169',
      error: '#e53e3e',
      warning: '#dd6b20',
    },
    dark: {
      // Backgrounds
      bgPrimary: '#0f172a',
      bgSecondary: '#1e293b',
      bgTertiary: '#334155',
      bgCard: '#1e293b',
      bgModal: 'rgba(30, 41, 59, 0.95)',
      bgOverlay: 'rgba(0, 0, 0, 0.7)',
      
      // Text
      textPrimary: '#f1f5f9',
      textSecondary: '#cbd5e1',
      textTertiary: '#94a3b8',
      textLink: '#60a5fa',
      textLinkHover: '#93c5fd',
      
      // Borders
      borderPrimary: '#334155',
      borderSecondary: '#475569',
      borderFocus: '#60a5fa',
      
      // Brand Colors
      brandPrimary: '#3b82f6',
      brandSecondary: '#1e40af',
      brandAccent: '#f59e0b',
      brandGradient: 'linear-gradient(135deg, #1e40af 0%, #3b82f6 50%, #f59e0b 100%)',
      
      // Inputs
      inputBg: '#1e293b',
      inputBorder: '#334155',
      inputBorderFocus: '#60a5fa',
      inputText: '#f1f5f9',
      inputPlaceholder: '#64748b',
      
      // Buttons - Premium contrast colors
      buttonPrimaryBg: 'linear-gradient(135deg, #1e40af 0%, #3b82f6 50%, #f59e0b 100%)',
      buttonPrimaryText: '#ffffff',
      buttonSecondaryBg: '#1e293b',
      buttonSecondaryText: '#60a5fa',
      buttonSecondaryBorder: '#60a5fa',
      buttonSuccessBg: 'linear-gradient(135deg, #059669 0%, #10b981 100%)',
      buttonSuccessText: '#ffffff',
      buttonDangerBg: 'linear-gradient(135deg, #dc2626 0%, #ef4444 100%)',
      buttonDangerText: '#ffffff',
      buttonDisabledBg: '#334155',
      buttonDisabledText: '#64748b',
      
      // Icon backgrounds
      iconBg: 'linear-gradient(135deg, #1e40af 0%, #3b82f6 100%)',
      iconText: '#ffffff',
      
      // Shadows
      shadowCard: '0 4px 20px rgba(0, 0, 0, 0.4)',
      shadowElevated: '0 10px 40px rgba(0, 0, 0, 0.5)',
      shadowButton: '0 4px 12px rgba(59, 130, 246, 0.4)',
      
      // Status
      success: '#4ade80',
      error: '#f87171',
      warning: '#fb923c',
    },
  },
  
  luxury: {
    name: 'Luxury Tech',
    description: 'Premium navy slate with champagne gold accents',
    light: {
      // Backgrounds
      bgPrimary: '#F8FAFC',
      bgSecondary: '#F1F5F9',
      bgTertiary: '#E2E8F0',
      bgCard: '#FFFFFF',
      bgModal: 'rgba(255, 255, 255, 0.98)',
      bgOverlay: 'rgba(11, 21, 40, 0.6)',
      
      // Text
      textPrimary: '#0F172A',
      textSecondary: '#475569',
      textTertiary: '#64748B',
      textLink: '#D4AF37',
      textLinkHover: '#B8941F',
      
      // Borders
      borderPrimary: '#E2E8F0',
      borderSecondary: '#CBD5E1',
      borderFocus: '#D4AF37',
      
      // Brand Colors
      brandPrimary: '#D4AF37',
      brandSecondary: '#B8941F',
      brandAccent: '#F59E0B',
      brandGradient: 'linear-gradient(135deg, #D4AF37 0%, #F59E0B 100%)',
      
      // Inputs
      inputBg: '#FFFFFF',
      inputBorder: '#E2E8F0',
      inputBorderFocus: '#D4AF37',
      inputText: '#0F172A',
      inputPlaceholder: '#94A3B8',
      
      // Buttons - Premium contrast colors
      buttonPrimaryBg: 'linear-gradient(135deg, #D4AF37 0%, #F59E0B 100%)',
      buttonPrimaryText: '#0B1528',
      buttonSecondaryBg: '#FFFFFF',
      buttonSecondaryText: '#D4AF37',
      buttonSecondaryBorder: '#D4AF37',
      buttonSuccessBg: 'linear-gradient(135deg, #059669 0%, #10b981 100%)',
      buttonSuccessText: '#ffffff',
      buttonDangerBg: 'linear-gradient(135deg, #dc2626 0%, #ef4444 100%)',
      buttonDangerText: '#ffffff',
      buttonDisabledBg: '#E2E8F0',
      buttonDisabledText: '#94A3B8',
      
      // Icon backgrounds
      iconBg: 'linear-gradient(135deg, #D4AF37 0%, #F59E0B 100%)',
      iconText: '#0B1528',
      
      // Shadows
      shadowCard: '0 2px 12px rgba(11, 21, 40, 0.06)',
      shadowElevated: '0 8px 32px rgba(11, 21, 40, 0.1)',
      shadowButton: '0 4px 16px rgba(212, 175, 55, 0.25)',
      
      // Status
      success: '#10B981',
      error: '#EF4444',
      warning: '#F59E0B',
    },
    dark: {
      // Backgrounds
      bgPrimary: '#0B1528',
      bgSecondary: '#111D35',
      bgTertiary: '#1A2942',
      bgCard: '#111D35',
      bgModal: 'rgba(17, 29, 53, 0.98)',
      bgOverlay: 'rgba(0, 0, 0, 0.75)',
      
      // Text
      textPrimary: '#E6EDF8',
      textSecondary: '#CBD5E1',
      textTertiary: '#94A3B8',
      textLink: '#D4AF37',
      textLinkHover: '#F5C842',
      
      // Borders
      borderPrimary: '#1A2942',
      borderSecondary: '#243352',
      borderFocus: '#D4AF37',
      
      // Brand Colors
      brandPrimary: '#D4AF37',
      brandSecondary: '#F59E0B',
      brandAccent: '#F5C842',
      brandGradient: 'linear-gradient(135deg, #D4AF37 0%, #F59E0B 100%)',
      
      // Inputs
      inputBg: '#111D35',
      inputBorder: '#1A2942',
      inputBorderFocus: '#D4AF37',
      inputText: '#E6EDF8',
      inputPlaceholder: '#64748B',
      
      // Buttons - Premium contrast colors
      buttonPrimaryBg: 'linear-gradient(135deg, #D4AF37 0%, #F59E0B 100%)',
      buttonPrimaryText: '#0B1528',
      buttonSecondaryBg: '#1A2942',
      buttonSecondaryText: '#D4AF37',
      buttonSecondaryBorder: '#D4AF37',
      buttonSuccessBg: 'linear-gradient(135deg, #059669 0%, #10b981 100%)',
      buttonSuccessText: '#ffffff',
      buttonDangerBg: 'linear-gradient(135deg, #dc2626 0%, #ef4444 100%)',
      buttonDangerText: '#ffffff',
      buttonDisabledBg: '#1A2942',
      buttonDisabledText: '#64748B',
      
      // Icon backgrounds
      iconBg: 'linear-gradient(135deg, #D4AF37 0%, #F59E0B 100%)',
      iconText: '#0B1528',
      
      // Shadows
      shadowCard: '0 2px 12px rgba(0, 0, 0, 0.3)',
      shadowElevated: '0 8px 32px rgba(0, 0, 0, 0.4)',
      shadowButton: '0 4px 16px rgba(212, 175, 55, 0.35)',
      
      // Status
      success: '#34D399',
      error: '#F87171',
      warning: '#FBBF24',
    },
  },
  
  enterprise: {
    name: 'Clean Enterprise',
    description: 'Stripe/Apple-inspired clean, trustworthy design',
    light: {
      // Backgrounds
      bgPrimary: '#FFFFFF',
      bgSecondary: '#F8FAFC',
      bgTertiary: '#F1F5F9',
      bgCard: '#FFFFFF',
      bgModal: 'rgba(255, 255, 255, 0.98)',
      bgOverlay: 'rgba(15, 23, 42, 0.5)',
      
      // Text
      textPrimary: '#0F172A',
      textSecondary: '#475569',
      textTertiary: '#64748B',
      textLink: '#4F46E5',
      textLinkHover: '#4338CA',
      
      // Borders
      borderPrimary: '#E2E8F0',
      borderSecondary: '#CBD5E1',
      borderFocus: '#4F46E5',
      
      // Brand Colors
      brandPrimary: '#4F46E5',
      brandSecondary: '#4338CA',
      brandAccent: '#6366F1',
      brandGradient: 'linear-gradient(135deg, #4F46E5 0%, #6366F1 100%)',
      
      // Inputs
      inputBg: '#FFFFFF',
      inputBorder: '#E2E8F0',
      inputBorderFocus: '#4F46E5',
      inputText: '#0F172A',
      inputPlaceholder: '#94A3B8',
      
      // Buttons - Premium contrast colors
      buttonPrimaryBg: 'linear-gradient(135deg, #4F46E5 0%, #6366F1 100%)',
      buttonPrimaryText: '#FFFFFF',
      buttonSecondaryBg: '#FFFFFF',
      buttonSecondaryText: '#4F46E5',
      buttonSecondaryBorder: '#4F46E5',
      buttonSuccessBg: 'linear-gradient(135deg, #059669 0%, #10b981 100%)',
      buttonSuccessText: '#ffffff',
      buttonDangerBg: 'linear-gradient(135deg, #dc2626 0%, #ef4444 100%)',
      buttonDangerText: '#ffffff',
      buttonDisabledBg: '#E2E8F0',
      buttonDisabledText: '#94A3B8',
      
      // Icon backgrounds
      iconBg: 'linear-gradient(135deg, #4F46E5 0%, #6366F1 100%)',
      iconText: '#FFFFFF',
      
      // Shadows
      shadowCard: '0 1px 8px rgba(15, 23, 42, 0.04)',
      shadowElevated: '0 4px 24px rgba(15, 23, 42, 0.08)',
      shadowButton: '0 4px 12px rgba(79, 70, 229, 0.2)',
      
      // Status
      success: '#10B981',
      error: '#EF4444',
      warning: '#F59E0B',
    },
    dark: {
      // Backgrounds
      bgPrimary: '#0F172A',
      bgSecondary: '#1E293B',
      bgTertiary: '#334155',
      bgCard: '#1E293B',
      bgModal: 'rgba(30, 41, 59, 0.98)',
      bgOverlay: 'rgba(0, 0, 0, 0.7)',
      
      // Text
      textPrimary: '#F8FAFC',
      textSecondary: '#CBD5E1',
      textTertiary: '#94A3B8',
      textLink: '#818CF8',
      textLinkHover: '#A5B4FC',
      
      // Borders
      borderPrimary: '#334155',
      borderSecondary: '#475569',
      borderFocus: '#818CF8',
      
      // Brand Colors
      brandPrimary: '#6366F1',
      brandSecondary: '#818CF8',
      brandAccent: '#A5B4FC',
      brandGradient: 'linear-gradient(135deg, #6366F1 0%, #818CF8 100%)',
      
      // Inputs
      inputBg: '#1E293B',
      inputBorder: '#334155',
      inputBorderFocus: '#818CF8',
      inputText: '#F8FAFC',
      inputPlaceholder: '#64748B',
      
      // Buttons - Premium contrast colors
      buttonPrimaryBg: 'linear-gradient(135deg, #6366F1 0%, #818CF8 100%)',
      buttonPrimaryText: '#FFFFFF',
      buttonSecondaryBg: '#1E293B',
      buttonSecondaryText: '#818CF8',
      buttonSecondaryBorder: '#818CF8',
      buttonSuccessBg: 'linear-gradient(135deg, #059669 0%, #10b981 100%)',
      buttonSuccessText: '#ffffff',
      buttonDangerBg: 'linear-gradient(135deg, #dc2626 0%, #ef4444 100%)',
      buttonDangerText: '#ffffff',
      buttonDisabledBg: '#334155',
      buttonDisabledText: '#64748B',
      
      // Icon backgrounds
      iconBg: 'linear-gradient(135deg, #6366F1 0%, #818CF8 100%)',
      iconText: '#FFFFFF',
      
      // Shadows
      shadowCard: '0 1px 8px rgba(0, 0, 0, 0.25)',
      shadowElevated: '0 4px 24px rgba(0, 0, 0, 0.35)',
      shadowButton: '0 4px 12px rgba(99, 102, 241, 0.3)',
      
      // Status
      success: '#34D399',
      error: '#F87171',
      warning: '#FBBF24',
    },
  },
  
  neo: {
    name: 'Modern Neo-Brutalist',
    description: 'Futuristic obsidian black with neon emerald accents',
    light: {
      // Backgrounds
      bgPrimary: '#F8FAFC',
      bgSecondary: '#F1F5F9',
      bgTertiary: '#E2E8F0',
      bgCard: '#FFFFFF',
      bgModal: 'rgba(255, 255, 255, 0.98)',
      bgOverlay: 'rgba(18, 18, 20, 0.6)',
      
      // Text
      textPrimary: '#0F172A',
      textSecondary: '#475569',
      textTertiary: '#64748B',
      textLink: '#10B981',
      textLinkHover: '#059669',
      
      // Borders
      borderPrimary: '#E2E8F0',
      borderSecondary: '#CBD5E1',
      borderFocus: '#10B981',
      
      // Brand Colors
      brandPrimary: '#10B981',
      brandSecondary: '#059669',
      brandAccent: '#34D399',
      brandGradient: 'linear-gradient(135deg, #10B981 0%, #34D399 100%)',
      
      // Inputs
      inputBg: '#FFFFFF',
      inputBorder: '#E2E8F0',
      inputBorderFocus: '#10B981',
      inputText: '#0F172A',
      inputPlaceholder: '#94A3B8',
      
      // Buttons - Premium contrast colors
      buttonPrimaryBg: 'linear-gradient(135deg, #10B981 0%, #34D399 100%)',
      buttonPrimaryText: '#0F172A',
      buttonSecondaryBg: '#FFFFFF',
      buttonSecondaryText: '#10B981',
      buttonSecondaryBorder: '#10B981',
      buttonSuccessBg: 'linear-gradient(135deg, #059669 0%, #10b981 100%)',
      buttonSuccessText: '#ffffff',
      buttonDangerBg: 'linear-gradient(135deg, #dc2626 0%, #ef4444 100%)',
      buttonDangerText: '#ffffff',
      buttonDisabledBg: '#E2E8F0',
      buttonDisabledText: '#94A3B8',
      
      // Icon backgrounds
      iconBg: 'linear-gradient(135deg, #10B981 0%, #34D399 100%)',
      iconText: '#0F172A',
      
      // Shadows
      shadowCard: '0 2px 10px rgba(18, 18, 20, 0.06)',
      shadowElevated: '0 8px 28px rgba(18, 18, 20, 0.1)',
      shadowButton: '0 4px 14px rgba(16, 185, 129, 0.25)',
      
      // Status
      success: '#10B981',
      error: '#EF4444',
      warning: '#F59E0B',
    },
    dark: {
      // Backgrounds
      bgPrimary: '#121214',
      bgSecondary: '#1A1A1D',
      bgTertiary: '#242428',
      bgCard: '#1A1A1D',
      bgModal: 'rgba(26, 26, 29, 0.98)',
      bgOverlay: 'rgba(0, 0, 0, 0.8)',
      
      // Text
      textPrimary: '#F8FAFC',
      textSecondary: '#CBD5E1',
      textTertiary: '#94A3B8',
      textLink: '#34D399',
      textLinkHover: '#6EE7B7',
      
      // Borders
      borderPrimary: '#242428',
      borderSecondary: '#2D2D35',
      borderFocus: '#34D399',
      
      // Brand Colors
      brandPrimary: '#10B981',
      brandSecondary: '#34D399',
      brandAccent: '#6EE7B7',
      brandGradient: 'linear-gradient(135deg, #10B981 0%, #34D399 100%)',
      
      // Inputs
      inputBg: '#1A1A1D',
      inputBorder: '#242428',
      inputBorderFocus: '#34D399',
      inputText: '#F8FAFC',
      inputPlaceholder: '#64748B',
      
      // Buttons - Premium contrast colors
      buttonPrimaryBg: 'linear-gradient(135deg, #10B981 0%, #34D399 100%)',
      buttonPrimaryText: '#121214',
      buttonSecondaryBg: '#242428',
      buttonSecondaryText: '#34D399',
      buttonSecondaryBorder: '#34D399',
      buttonSuccessBg: 'linear-gradient(135deg, #059669 0%, #10b981 100%)',
      buttonSuccessText: '#ffffff',
      buttonDangerBg: 'linear-gradient(135deg, #dc2626 0%, #ef4444 100%)',
      buttonDangerText: '#ffffff',
      buttonDisabledBg: '#242428',
      buttonDisabledText: '#64748B',
      
      // Icon backgrounds
      iconBg: 'linear-gradient(135deg, #10B981 0%, #34D399 100%)',
      iconText: '#121214',
      
      // Shadows
      shadowCard: '0 2px 10px rgba(0, 0, 0, 0.35)',
      shadowElevated: '0 8px 28px rgba(0, 0, 0, 0.45)',
      shadowButton: '0 4px 14px rgba(16, 185, 129, 0.4)',
      
      // Status
      success: '#34D399',
      error: '#F87171',
      warning: '#FBBF24',
    },
  },
  
  midnight: {
    name: 'Midnight',
    description: 'Elegant purple and teal theme',
    light: {
      // Backgrounds
      bgPrimary: '#faf5ff',
      bgSecondary: '#f3e8ff',
      bgTertiary: '#e9d5ff',
      bgCard: '#ffffff',
      bgModal: 'rgba(255, 255, 255, 0.95)',
      bgOverlay: 'rgba(0, 0, 0, 0.5)',
      
      // Text
      textPrimary: '#1e1b4b',
      textSecondary: '#4c1d95',
      textTertiary: '#6b7280',
      textLink: '#7c3aed',
      textLinkHover: '#5b21b6',
      
      // Borders
      borderPrimary: '#e9d5ff',
      borderSecondary: '#d8b4fe',
      borderFocus: '#7c3aed',
      
      // Brand Colors
      brandPrimary: '#7c3aed',
      brandSecondary: '#5b21b6',
      brandAccent: '#14b8a6',
      brandGradient: 'linear-gradient(135deg, #5b21b6 0%, #7c3aed 50%, #14b8a6 100%)',
      
      // Inputs
      inputBg: '#ffffff',
      inputBorder: '#e9d5ff',
      inputBorderFocus: '#7c3aed',
      inputText: '#1e1b4b',
      inputPlaceholder: '#a78bfa',
      
      // Buttons - Premium contrast colors
      buttonPrimaryBg: 'linear-gradient(135deg, #5b21b6 0%, #7c3aed 50%, #14b8a6 100%)',
      buttonPrimaryText: '#ffffff',
      buttonSecondaryBg: '#ffffff',
      buttonSecondaryText: '#7c3aed',
      buttonSecondaryBorder: '#7c3aed',
      buttonSuccessBg: 'linear-gradient(135deg, #059669 0%, #10b981 100%)',
      buttonSuccessText: '#ffffff',
      buttonDangerBg: 'linear-gradient(135deg, #dc2626 0%, #ef4444 100%)',
      buttonDangerText: '#ffffff',
      buttonDisabledBg: '#e9d5ff',
      buttonDisabledText: '#a78bfa',
      
      // Icon backgrounds
      iconBg: 'linear-gradient(135deg, #5b21b6 0%, #7c3aed 100%)',
      iconText: '#ffffff',
      
      // Shadows
      shadowCard: '0 4px 20px rgba(124, 58, 237, 0.1)',
      shadowElevated: '0 10px 40px rgba(124, 58, 237, 0.15)',
      shadowButton: '0 4px 12px rgba(124, 58, 237, 0.3)',
      
      // Status
      success: '#10b981',
      error: '#ef4444',
      warning: '#f59e0b',
    },
    dark: {
      // Backgrounds
      bgPrimary: '#0a0a0f',
      bgSecondary: '#13131f',
      bgTertiary: '#1e1e2e',
      bgCard: '#13131f',
      bgModal: 'rgba(19, 19, 31, 0.95)',
      bgOverlay: 'rgba(0, 0, 0, 0.7)',
      
      // Text
      textPrimary: '#e2e8f0',
      textSecondary: '#a5b4fc',
      textTertiary: '#818cf8',
      textLink: '#a78bfa',
      textLinkHover: '#c4b5fd',
      
      // Borders
      borderPrimary: '#1e1e2e',
      borderSecondary: '#2d2d44',
      borderFocus: '#a78bfa',
      
      // Brand Colors
      brandPrimary: '#8b5cf6',
      brandSecondary: '#6d28d9',
      brandAccent: '#2dd4bf',
      brandGradient: 'linear-gradient(135deg, #6d28d9 0%, #8b5cf6 50%, #2dd4bf 100%)',
      
      // Inputs
      inputBg: '#13131f',
      inputBorder: '#1e1e2e',
      inputBorderFocus: '#a78bfa',
      inputText: '#e2e8f0',
      inputPlaceholder: '#6b7280',
      
      // Buttons - Premium contrast colors
      buttonPrimaryBg: 'linear-gradient(135deg, #6d28d9 0%, #8b5cf6 50%, #2dd4bf 100%)',
      buttonPrimaryText: '#ffffff',
      buttonSecondaryBg: '#13131f',
      buttonSecondaryText: '#a78bfa',
      buttonSecondaryBorder: '#a78bfa',
      buttonSuccessBg: 'linear-gradient(135deg, #059669 0%, #10b981 100%)',
      buttonSuccessText: '#ffffff',
      buttonDangerBg: 'linear-gradient(135deg, #dc2626 0%, #ef4444 100%)',
      buttonDangerText: '#ffffff',
      buttonDisabledBg: '#1e1e2e',
      buttonDisabledText: '#6b7280',
      
      // Icon backgrounds
      iconBg: 'linear-gradient(135deg, #6d28d9 0%, #8b5cf6 100%)',
      iconText: '#ffffff',
      
      // Shadows
      shadowCard: '0 4px 20px rgba(139, 92, 246, 0.3)',
      shadowElevated: '0 10px 40px rgba(139, 92, 246, 0.4)',
      shadowButton: '0 4px 12px rgba(139, 92, 246, 0.4)',
      
      // Status
      success: '#34d399',
      error: '#f87171',
      warning: '#fbbf24',
    },
  },
  
  emerald: {
    name: 'Emerald',
    description: 'Modern green and slate theme',
    light: {
      // Backgrounds
      bgPrimary: '#f0fdf4',
      bgSecondary: '#dcfce7',
      bgTertiary: '#bbf7d0',
      bgCard: '#ffffff',
      bgModal: 'rgba(255, 255, 255, 0.95)',
      bgOverlay: 'rgba(0, 0, 0, 0.5)',
      
      // Text
      textPrimary: '#14532d',
      textSecondary: '#166534',
      textTertiary: '#6b7280',
      textLink: '#16a34a',
      textLinkHover: '#15803d',
      
      // Borders
      borderPrimary: '#bbf7d0',
      borderSecondary: '#86efac',
      borderFocus: '#16a34a',
      
      // Brand Colors
      brandPrimary: '#16a34a',
      brandSecondary: '#15803d',
      brandAccent: '#0ea5e9',
      brandGradient: 'linear-gradient(135deg, #15803d 0%, #16a34a 50%, #0ea5e9 100%)',
      
      // Inputs
      inputBg: '#ffffff',
      inputBorder: '#bbf7d0',
      inputBorderFocus: '#16a34a',
      inputText: '#14532d',
      inputPlaceholder: '#86efac',
      
      // Buttons - Premium contrast colors
      buttonPrimaryBg: 'linear-gradient(135deg, #15803d 0%, #16a34a 50%, #0ea5e9 100%)',
      buttonPrimaryText: '#ffffff',
      buttonSecondaryBg: '#ffffff',
      buttonSecondaryText: '#16a34a',
      buttonSecondaryBorder: '#16a34a',
      buttonSuccessBg: 'linear-gradient(135deg, #059669 0%, #10b981 100%)',
      buttonSuccessText: '#ffffff',
      buttonDangerBg: 'linear-gradient(135deg, #dc2626 0%, #ef4444 100%)',
      buttonDangerText: '#ffffff',
      buttonDisabledBg: '#bbf7d0',
      buttonDisabledText: '#86efac',
      
      // Icon backgrounds
      iconBg: 'linear-gradient(135deg, #15803d 0%, #16a34a 100%)',
      iconText: '#ffffff',
      
      // Shadows
      shadowCard: '0 4px 20px rgba(22, 163, 74, 0.1)',
      shadowElevated: '0 10px 40px rgba(22, 163, 74, 0.15)',
      shadowButton: '0 4px 12px rgba(22, 163, 74, 0.3)',
      
      // Status
      success: '#10b981',
      error: '#ef4444',
      warning: '#f59e0b',
    },
    dark: {
      // Backgrounds
      bgPrimary: '#022c22',
      bgSecondary: '#064e3b',
      bgTertiary: '#065f46',
      bgCard: '#064e3b',
      bgModal: 'rgba(6, 78, 59, 0.95)',
      bgOverlay: 'rgba(0, 0, 0, 0.7)',
      
      // Text
      textPrimary: '#ecfdf5',
      textSecondary: '#a7f3d0',
      textTertiary: '#6ee7b7',
      textLink: '#34d399',
      textLinkHover: '#6ee7b7',
      
      // Borders
      borderPrimary: '#065f46',
      borderSecondary: '#047857',
      borderFocus: '#34d399',
      
      // Brand Colors
      brandPrimary: '#10b981',
      brandSecondary: '#059669',
      brandAccent: '#06b6d4',
      brandGradient: 'linear-gradient(135deg, #059669 0%, #10b981 50%, #06b6d4 100%)',
      
      // Inputs
      inputBg: '#064e3b',
      inputBorder: '#065f46',
      inputBorderFocus: '#34d399',
      inputText: '#ecfdf5',
      inputPlaceholder: '#6b7280',
      
      // Buttons - Premium contrast colors
      buttonPrimaryBg: 'linear-gradient(135deg, #059669 0%, #10b981 50%, #06b6d4 100%)',
      buttonPrimaryText: '#ffffff',
      buttonSecondaryBg: '#064e3b',
      buttonSecondaryText: '#34d399',
      buttonSecondaryBorder: '#34d399',
      buttonSuccessBg: 'linear-gradient(135deg, #059669 0%, #10b981 100%)',
      buttonSuccessText: '#ffffff',
      buttonDangerBg: 'linear-gradient(135deg, #dc2626 0%, #ef4444 100%)',
      buttonDangerText: '#ffffff',
      buttonDisabledBg: '#065f46',
      buttonDisabledText: '#6b7280',
      
      // Icon backgrounds
      iconBg: 'linear-gradient(135deg, #059669 0%, #10b981 100%)',
      iconText: '#ffffff',
      
      // Shadows
      shadowCard: '0 4px 20px rgba(16, 185, 129, 0.3)',
      shadowElevated: '0 10px 40px rgba(16, 185, 129, 0.4)',
      shadowButton: '0 4px 12px rgba(16, 185, 129, 0.4)',
      
      // Status
      success: '#34d399',
      error: '#f87171',
      warning: '#fbbf24',
    },
  },
};

/**
 * Get default theme from environment variable
 * Falls back to 'ocean' if not specified
 */
export const getDefaultTheme = () => {
  return process.env.REACT_APP_DEFAULT_THEME || 'ocean';
};

/**
 * Get theme configuration by name
 */
export const getThemeConfig = (themeName) => {
  return THEMES[themeName] || THEMES.ocean;
};

/**
 * Get all available theme names
 */
export const getAvailableThemes = () => {
  return Object.keys(THEMES);
};
